import { useCallback } from 'react'

import { Theme } from 'src/apollo/sites'

import {
  useOptionalRestaurant,
  useRestaurant
} from 'shared/components/common/restaurant_context/RestaurantContext'
import { generateTextColor } from 'public/components/default_template/online_ordering/item_tags/tagColorUtils'

type ThemeConsumerType<T> = (theme: Theme) => T

const getThemeColorScheme = <T,>(
  theme: Theme,
  themeConsumer: ThemeConsumerType<T>,
  defaultColor: T
) => {
  try {
    if (theme?.enabled && theme?.colorScheme) {
      return themeConsumer(theme)
    } else {
      return defaultColor
    }
  } catch (_) {
    return defaultColor
  }
}

export const useThemeColorScheme = () => {
  const {
    restaurant: { theme }
  } = useRestaurant()

  return useCallback(
    <T,>(themeConsumer: ThemeConsumerType<T>, defaultColor: T) => {
      return getThemeColorScheme(theme as Theme, themeConsumer, defaultColor)
    },
    [theme]
  )
}

export const useOptionalThemeColorScheme = () => {
  const theme = useOptionalRestaurant()?.restaurant?.theme

  return useCallback(
    <T,>(themeConsumer: ThemeConsumerType<T>, defaultColor: T) => {
      return getThemeColorScheme(theme as Theme, themeConsumer, defaultColor)
    },
    [theme]
  )
}

export const useIsDarkMode = () => {
  const getColorFromTheme = useThemeColorScheme()
  const {
    restaurant: { meta }
  } = useRestaurant()
  // If theming is turned on, check the variant.  If not, we can assume the background is dark if the generated text color for it is white
  const darkMode = getColorFromTheme(
    (theme) => theme.variant.startsWith('dark'),
    meta?.backgroundColor
      ? generateTextColor(meta.backgroundColor).toLowerCase() === '#ffffff'
      : false
  )
  return darkMode
}
